import { useEffect, useState } from "react";
import { Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout } from "ui-components/Layout";
import { EditButton, DelButton } from "ui-components/IconButton";
import { Success, Error_, MessageDialog } from "ui-components/Alert";
import { getCustomerList } from "services/data.internal.service";
import { MoreButton } from "ui-components/IconButton";
import { getCustomerStatusCount } from "services/data.internal.service";
import BasicTabs from "ui-components/BasicTabs";
import { TableCard } from "ui-components/Layout";
import { ActiveButton } from "ui-components/IconButton";
import { deactivateCustomer } from "services/data.internal.service";
import { activeCustomer } from "services/data.internal.service";
import { useTeamValue } from "providers/TeamContext";
import { CSVLink } from "react-csv";
import { useCustomerValue } from "providers/CustomerContext";
import { ExportDialog } from "ui-components/Alert";
import dayjs from "dayjs";
import useCsvDownloadHook from "hooks/useCsvDownloadHook";

function CustomerList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue()
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "InActive", count: 0 },
  ]);
  const [data, setData] = useState([]);
  const queryClient = useQueryClient();
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const onSuccess = (data) => {
    queryClient.invalidateQueries("customer-list");
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const { downloadCsv, isDownloading } = useCsvDownloadHook();

  useEffect(() => {
    if (location.state) {
      setAlert(<Success content={location.state} onChange={setAlert}/>)
    }
  }, []);

  const [filteredData, setFilteredData] = useState([]);

  const addPermission =
    allowAll ||
    (Boolean(permissions["Customer"]) &&
      permissions["Customer"].findIndex((b) => b === "new") !== -1);
  const editPermission =
    allowAll ||
    (Boolean(permissions["Customer"]) &&
      permissions["Customer"].findIndex((b) => b === "edit") !== -1);
  const deactivatePermission =
    allowAll ||
    (Boolean(permissions["Customer"]) &&
      permissions["Customer"].findIndex((b) => b === "deactivate") !== -1);

  const addWHPermission =
    allowAll ||
    (Boolean(permissions["Warehouse Setting"]) &&
      permissions["Warehouse Setting"].findIndex((b) => b === "new") !== -1);
  const addCarrierPermission =
    allowAll ||
    (Boolean(permissions["Carrier Setting"]) &&
      permissions["Carrier Setting"].findIndex((b) => b === "new") !== -1);

  useQuery({
    queryKey: ["customer-list", { customerCode, active }],
    queryFn: () => getCustomerList({ active }),
    onSuccess: (data) => {
      setFilteredData(data.data.data);
      setData(data.data.data);
    },
    onError,
  });

  useQuery({
    queryKey: ["customer-list", "status", { customerCode }],
    queryFn: () => getCustomerStatusCount(),
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deactivateCustomer, { onSuccess, onError });
  const activeMutation = useMutation(activeCustomer, { onSuccess, onError });

  const onEdit = (custCode) => navigate("/setup/customer/edit", { state: { customerCode: custCode } });

  const onDelete = (custCode) => {
    setDialog(
      <MessageDialog
        title={`Deactivate ${custCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate({ customerCode: custCode });
          setDialog(null);
        }}
      />
    );
  };
  const onActive = (custCode) => {
    setDialog(
      <MessageDialog
        title={`Activate ${custCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate({ customerCode: custCode, currentCustCode: customerCode });
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "Customer Code", accessorKey: "customerCode" },
    { header: "Company Code", accessorKey: "companyCode" },
    { header: "Customer Name", accessorKey: "customerName" },
    { header: "Short Name", accessorKey: "custShortName" },
    { header: "Customer Type", accessorKey: "customerType" },
    { header: "Registration No", accessorKey: "registrationNo" },
    { header: "Company Url", accessorKey: "companyUrl" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { customerCode: custCode } = row.original;

        if (active) {
          return (
            <>
              <EditButton
                title="edit customer"
                disabled={!editPermission}
                onClick={() => onEdit(custCode)}
              />
              <DelButton
                title="deactivate customer"
                disabled={!deactivatePermission}
                onClick={() => onDelete(custCode)}
              />

              <MoreButton
                options={[
                  {
                    label: "Warehouse Setting",
                    disabled: !addWHPermission,
                    onClick: () => {
                      navigate("/setup/customer/wh-setting/add", {
                        state: { custCode, customerCode: custCode },
                      });
                    },
                  },
                  // {
                  //   label: "Carrier Setting",
                  //   onClick: () => {
                  //     navigate("/setup/cust-carrier/add", {
                  //       state: { custCode: customerCode },
                  //     });
                  //   },
                  // },
                  {
                    label: "Carrier Setting",
                    disabled: !addCarrierPermission,
                    onClick: () => {
                      navigate("/setup/customer/carrier/add", {
                        state: { customerCode: custCode },
                      });
                    },
                  },
                  {
                    label: "API Setting",
                    onClick: () => {
                      const { customerCode: custCode, asKey } = row.original;
                      navigate("/setup/customer/api-setting/add", {
                        state: { customerCode: custCode, apiSecretKey: asKey || "" }
                      });
                    },
                  },
                ]}
              />
            </>
          );
        } else {
        }
        return <ActiveButton title="activate customer" onClick={() => onActive(custCode)} />;
      },
    },
  ];

  const handleFilterRowsChange = (filteredData) => {
    setFilteredData(filteredData);
  };

  return (
    <ListLayout>
      {isDownloading && <ExportDialog />}
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>

      <TableCard>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" margin="10px">
          <MDButton
            variant="gradient"
            color="info"
            disabled={!addPermission}
            onClick={() => navigate("/setup/customer/add")}
          >
            <Icon>add</Icon>&nbsp;customer
          </MDButton>

          {/* <CSVLink filename="ExportCustomer_" data={filteredData}>
            <MDButton variant="outlined" color="dark" title="export customer">
              <Icon>download</Icon>&nbsp;export
            </MDButton>
          </CSVLink> */}
          <MDButton
            variant="outlined" color="dark" title="export customer"
            onClick={() => {
              const filename = `${customerCode}_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_ExportCustomer`;
              downloadCsv(filename, filteredData)
            }}
          >
            <Icon>download</Icon>&nbsp;export
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={data} onFilterRowsChange={handleFilterRowsChange} />
      </TableCard>
    </ListLayout>
  );
}

export default CustomerList;
